"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { SliderContext } from "./helpers/SliderContext.mjs";
import Label from "@ingka/label";
import cx from "classnames";
import noUiSlider from "nouislider";
import {
  updateAriaLabels,
  reflectHandleStyles
} from "@ingka/slider-shared/utils/slider-extensions";
import Tooltips from "./elements/Tooltips.mjs";
import {
  elx,
  clamp,
  isEqualContent,
  sanitizeValues,
  isRTL
} from "@ingka/slider-shared/utils/tools";
import HelperText from "@ingka/helper-text";
const Slider = (props) => {
  const {
    start,
    step = 1,
    range,
    margin,
    prefix = "",
    ariaFormat,
    className,
    disabled,
    id,
    marks,
    prefixLabel = "",
    suffixLabel = "",
    inputControls,
    label,
    ariaLabels,
    format = {
      to: (num) => num.toString(),
      from: (str) => str.length === 0 || Number.isNaN(Number(str)) ? str : Number(str)
    },
    behaviour,
    onUpdate,
    onChange,
    onSet,
    onStartDrag,
    onEndDrag,
    onValueChange,
    validators,
    ...otherProps
  } = props;
  let rtl = props.rtl;
  delete otherProps.rtl;
  const ns = ".skapa";
  const values = sanitizeValues(start === void 0 ? range.min : start, [
    range.min,
    range.max
  ]);
  const [_values, setValues] = useState(values);
  const isRangeSelector = Array.isArray(start) && start.length > 1;
  const slider = useRef(null);
  const sliderDOM = useRef(
    null
  );
  const [appliedRTL, setAppliedRTL] = useState(rtl);
  const [isDragged, setDragged] = useState(false);
  const [validationMessages, setValidationMessages] = useState(["", ""]);
  const [currentErrorMessage, setCurrentErrorMessage] = useState("");
  const formFieldID = `${id}__slider-form-field`;
  const labelID = `${id}__label`;
  const labelForID = inputControls ? `${id}__rangeInput0` : id;
  if (step <= 0 || step > range.max - range.min)
    console.error(
      "Range field 'step' value must be a non-zero positive number that is smaller than the given range!"
    );
  if (range.max <= range.min) {
    console.error("Invalid range values");
    range.max = range.min + 1;
  }
  useEffect(() => {
    values.forEach((value, index) => {
      const failedValidator = validators && validators.find(
        (validator) => !(validator.validate && validator.validate({ value, index, values, range, step }))
      );
      const newValidationMessages = validationMessages;
      newValidationMessages[index] = failedValidator && (typeof failedValidator.message === "function" ? failedValidator.message({ value, index, values, range, step }) : failedValidator.message) || // or a simple static validation message
      "";
      setValidationMessages(newValidationMessages);
    });
    setCurrentErrorMessage(validationMessages.find(Boolean) || "");
  }, [values]);
  const initializeSlider = () => {
    if (sliderDOM.current) {
      if (sliderDOM.current.noUiSlider) {
        sliderDOM.current.noUiSlider.destroy();
      }
      if (rtl === void 0)
        rtl = isRTL(sliderDOM.current);
      setAppliedRTL(rtl);
      slider.current = noUiSlider.create(sliderDOM.current, {
        start: values,
        step,
        range,
        connect: isRangeSelector ? true : "lower",
        behaviour,
        margin,
        direction: rtl ? "rtl" : "ltr",
        cssPrefix: `${prefix}slider-`,
        ariaFormat,
        tooltips: false
      });
      updateAriaLabels(
        ariaLabels,
        labelID,
        id,
        sliderDOM.current,
        prefix,
        formFieldID
      );
    }
  };
  const bindSliderEvents = () => {
    const onSlide = (newValues) => {
      const numValues = newValues.map(Number);
      setValues(
        (previousValues) => !isEqualContent(numValues, previousValues) ? numValues : previousValues
      );
      if (onChange)
        onChange(numValues);
    };
    const handleValueChange = (newValues) => {
      if (onValueChange)
        onValueChange(newValues.map(Number));
    };
    const bindListener = (event, listener) => listener && slider.current?.on(event, listener);
    if (slider.current) {
      bindListener("update" + ns, onUpdate);
      bindListener("slide" + ns, onSlide);
      bindListener("set" + ns, onSet);
      bindListener(
        "start" + ns,
        elx(onStartDrag, () => setDragged(true))
      );
      bindListener(
        "end" + ns,
        elx(onEndDrag, () => setDragged(false))
      );
      bindListener("change" + ns, handleValueChange);
    }
  };
  useEffect(() => {
    initializeSlider();
    return () => {
      if (slider.current)
        slider.current.destroy();
    };
  }, [prefix, rtl]);
  useEffect(() => {
    bindSliderEvents();
    return () => {
      if (slider.current) {
        slider.current.off(ns);
      }
    };
  }, [
    onUpdate,
    onChange,
    onSet,
    onStartDrag,
    onEndDrag,
    range.min,
    range.max,
    appliedRTL
  ]);
  useEffect(() => {
    if (slider.current && slider.current.updateOptions) {
      slider.current.updateOptions({ margin, step, range }, false);
      updateAriaLabels(
        ariaLabels,
        labelID,
        id,
        sliderDOM.current,
        prefix,
        formFieldID
      );
    }
  }, [step, marks, range.min, range.max, disabled, ariaLabels, id]);
  useEffect(() => {
    if (slider.current) {
      const currentStart = [slider.current.get(true)].flat();
      if (currentStart.length !== values.length) {
        slider.current.off(ns);
        slider.current.destroy();
        initializeSlider();
        bindSliderEvents();
      } else {
        const previousValues = sanitizeValues(slider.current.get(), [
          range.min,
          range.max
        ]);
        if (!isEqualContent(values, previousValues)) {
          if (!isDragged)
            slider.current.set(values, false);
        }
      }
      setValues(
        (previousValues) => !isEqualContent(values, previousValues) ? values : previousValues
      );
    }
  }, [start]);
  const baseClass = `${prefix}slider`;
  const classes = cx(
    baseClass,
    values.length === 2 && `${baseClass}--dual-sliders`,
    inputControls && `${baseClass}--with-input`,
    disabled && `${baseClass}-disabled`,
    isDragged && `${baseClass}-drag`,
    marks && `${baseClass}--marks`,
    className
  );
  const sliderContext = {
    prefix,
    values: _values,
    range,
    rtl: appliedRTL || false,
    baseClass,
    baseId: id,
    validationMessages,
    step,
    validation: !!currentErrorMessage,
    format,
    ariaFormat,
    formFieldID,
    disabled: disabled || false,
    labelID,
    prefixLabel,
    suffixLabel,
    onValueEntered: (newValues) => {
      const sanitizedValue = sanitizeValues(newValues, [range.min, range.max]);
      setValues((previousValues) => {
        slider.current?.set(sanitizedValue);
        return !isEqualContent(newValues, previousValues) ? sanitizedValue : previousValues;
      });
      if (onChange)
        onChange(sanitizedValue);
      if (onValueChange) {
        const clamped = sanitizedValue.map(
          (v) => clamp(v, range.min, range.max)
        );
        if (isEqualContent(clamped, newValues))
          onValueChange(clamped);
      }
    }
  };
  const withInputLabels = !!inputControls;
  reflectHandleStyles(_values, range, sliderDOM.current, step);
  return /* @__PURE__ */ jsxs("div", { ...otherProps, className: classes, children: [
    /* @__PURE__ */ jsxs("div", { className: `${baseClass}__controls`, children: [
      /* @__PURE__ */ jsx(
        Label,
        {
          id: labelID,
          className: cx(
            `${baseClass}__label`,
            withInputLabels && isRangeSelector && `${baseClass}__label-dbm`
          ),
          prefix,
          htmlFor: labelForID,
          label
        }
      ),
      inputControls && /* @__PURE__ */ jsx(SliderContext.Provider, { value: sliderContext, children: inputControls })
    ] }),
    /* @__PURE__ */ jsx(
      HelperText,
      {
        id: formFieldID,
        prefix,
        className: cx(
          `${baseClass}__form-field`,
          `${prefix}form-field__content`
        ),
        shouldValidate: !!currentErrorMessage,
        valid: !currentErrorMessage,
        children: currentErrorMessage
      }
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        disabled,
        className: `${baseClass}__element`,
        id,
        ref: sliderDOM
      }
    ),
    !inputControls && !disabled && /* @__PURE__ */ jsx(SliderContext.Provider, { value: sliderContext, children: /* @__PURE__ */ jsx(
      Tooltips,
      {
        className: cx(
          `${baseClass}-tooltips`,
          isDragged && `${baseClass}-tooltips-drag`
        )
      }
    ) })
  ] });
};
var Slider_default = Slider;
export {
  Slider_default as default
};
